/**
 * One evidence file row: a thumbnail (or filetype icon), the filename, caller-
 * supplied classifier badges + meta line, and preview / download / delete
 * actions. Preview opens whatever dialog the parent wires to `onPreview`;
 * download defaults to a native anchor to the never-expiring file URL and can
 * be overridden.
 *
 * Purely presentational and data-source agnostic - the claim Evidence card and
 * the inspection Evidence tab both render this exact row so the two surfaces
 * stay visually identical.
 */
import type { ReactNode } from 'react'
import { Download, Eye, Loader2, Tag, Trash2 } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { SimpleTooltip } from '@/shared/components/ui/tooltip'
import { isImageValue } from '@/shared/lib/file-types'
import { fileTypeIcon } from '@/shared/components/evidence/evidence-file-utils'
import { fileDownloadUrl } from '@/shared/lib/authenticated-file'

export interface EvidenceFileRowProps {
  fileName: string
  fileUrl?: string | null
  /**
   * ``UserUploadedFile.uuid`` of a protected file. Kept for callers that key
   * preview off the file identity; ``fileUrl`` (the never-expiring proxy URL)
   * is what download uses (TA-23).
   */
  fileUuid?: string | null
  thumbnailUrl?: string | null
  /**
   * Pre-rendered thumbnail node (e.g. an ``<AuthenticatedImage>``). When
   * provided it replaces the bare ``<img src={thumbnailUrl}>`` - this is how
   * protected files show a thumbnail the browser cannot load unauthenticated.
   */
  thumbnail?: ReactNode
  mimeType?: string | null
  /** Classifier badges rendered beside the filename (category / kind / tags). */
  badges?: ReactNode
  /** Secondary meta line (size / timestamp / uploader). */
  meta?: ReactNode
  /** Soft-deleted styling (strikethrough, dimmed) + hides preview/download. */
  deleted?: boolean
  /** Extra note shown under the meta line for deleted rows. */
  deletedNote?: ReactNode
  /** When set (and the file is live with a url), enables the preview affordance. */
  onPreview?: () => void
  /** When set (and the file is live), shows the re-categorise action. */
  onRecategorise?: () => void
  /**
   * Tooltip/aria wording for the re-categorise action. Claims file evidence
   * into a fixed category taxonomy, while the generic attachments card uses
   * a free-text label, so the two surfaces name the same action differently.
   */
  recategoriseLabel?: string
  /** When set (and the file is live), shows the delete action. */
  onDelete?: () => void
  isDeleting?: boolean
  /** Override the default download-anchor behaviour. */
  onDownload?: () => void
}

export function EvidenceFileRow({
  fileName,
  fileUrl,
  fileUuid,
  thumbnailUrl,
  thumbnail,
  mimeType,
  badges,
  meta,
  deleted = false,
  deletedNote,
  onPreview,
  onRecategorise,
  recategoriseLabel = 'Change category',
  onDelete,
  isDeleting = false,
  onDownload,
}: EvidenceFileRowProps) {
  const isImage = isImageValue(mimeType, fileName)
  const hasSource = !!fileUrl || !!fileUuid
  const canPreview = !deleted && hasSource && !!onPreview
  const canDownload = !deleted && !!fileUrl

  const download = () => {
    if (onDownload) return onDownload()
    // ``fileUrl`` is the stable, never-expiring proxy URL (TA-23). Adding
    // ``download=1`` flips the server's Content-Disposition to ``attachment``,
    // so a plain anchor saves the file with its name - no fetch, no blob.
    const href = fileDownloadUrl(fileUrl)
    if (!href) return
    const a = document.createElement('a')
    a.href = href
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  return (
    <li className={`flex items-start gap-3 px-3 py-2.5 ${deleted ? 'opacity-50' : ''}`}>
      {/* Thumb / icon */}
      <Button
        type="button"
        variant="ghost"
        onClick={canPreview ? onPreview : undefined}
        className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[4px] border border-[var(--rule)] bg-[var(--bg-light)] p-0 ${
          canPreview
            ? 'cursor-zoom-in transition-opacity hover:opacity-80'
            : 'hover:bg-[var(--bg-light)]'
        }`}
        disabled={!canPreview}
        aria-label={canPreview ? `Preview ${fileName}` : undefined}
      >
        {thumbnail && isImage && !deleted ? (
          thumbnail
        ) : thumbnailUrl && isImage && !deleted ? (
          <img src={thumbnailUrl} alt={fileName} className="h-full w-full object-cover" />
        ) : (
          <span className="text-[var(--ink-3)]">{fileTypeIcon(mimeType)}</span>
        )}
      </Button>

      {/* Metadata */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-[12px] font-medium ${
              deleted ? 'line-through text-[var(--ink-3)]' : 'text-[var(--ink)]'
            }`}
          >
            {fileName}
          </span>
          {badges}
        </div>
        {meta ? (
          <div className="mt-1 flex items-center gap-2 text-[10px] text-[var(--ink-3)] flex-wrap">
            {meta}
          </div>
        ) : null}
        {deleted && deletedNote ? (
          <div className="mt-1 text-[10px] text-rose-700">{deletedNote}</div>
        ) : null}
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1">
        {canPreview ? (
          <SimpleTooltip content="Preview" compact>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={onPreview}
              aria-label="Preview"
            >
              <Eye className="h-3 w-3" />
            </Button>
          </SimpleTooltip>
        ) : null}
        {canDownload ? (
          <SimpleTooltip content="Download" compact>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={download}
              aria-label="Download"
            >
              <Download className="h-3 w-3" />
            </Button>
          </SimpleTooltip>
        ) : null}
        {onRecategorise && !deleted ? (
          <SimpleTooltip content={recategoriseLabel} compact>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={onRecategorise}
              aria-label={recategoriseLabel}
            >
              <Tag className="h-3 w-3" />
            </Button>
          </SimpleTooltip>
        ) : null}
        {onDelete && !deleted ? (
          <SimpleTooltip content="Delete" compact>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={onDelete}
              disabled={isDeleting}
              aria-label="Delete"
              className="text-rose-600 hover:bg-rose-50"
            >
              {isDeleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
            </Button>
          </SimpleTooltip>
        ) : null}
      </div>
    </li>
  )
}
